import { useTenant } from "@/tenant/TenantProvider";

/**
 * The one footer, used on every screen.
 *
 *   © 2026 LR Rewards · Powered by GatiQ Rewards · Support: +91… · help@…
 *
 * Every part comes from branding tokens, so it follows the inheritance rule
 * like everything else: an organization that set its own name and support
 * details shows those; one that set nothing shows the platform's.
 *
 * The two names are deliberately different sources. The copyright carries the
 * ORGANIZATION's name — it is their programme. "Powered by" carries the
 * PLATFORM's, from `platformAppName`, which is the super admin's setting and
 * cannot be overridden by a tenant.
 *
 * Segments with no value are omitted rather than rendered blank, so an org
 * that has not filled in a support email does not ship a stray separator.
 */
export function AppFooter({ className = "" }: { className?: string }) {
  const { payload } = useTenant();
  const brand = payload.branding;
  const year = new Date().getFullYear();

  const owner = payload.orgName ?? brand.appName ?? "";
  const platform = payload.platformAppName;
  const phone = brand.supportPhone ?? "";
  const email = brand.supportEmail ?? "";

  const parts: React.ReactNode[] = [];

  parts.push(<span key="copyright">© {year} {owner}</span>);

  // Only worth saying when the tenant is running under a differently-named
  // platform; "GatiQ · Powered by GatiQ" is noise.
  if (platform && platform !== owner) {
    parts.push(<span key="powered">Powered by {platform}</span>);
  }

  if (phone) {
    parts.push(
      <span key="phone">
        Support:{" "}
        <a className="text-ink transition-colors hover:underline" href={`tel:${phone.replace(/\s/g, "")}`}>
          {phone}
        </a>
      </span>,
    );
  }

  if (email) {
    parts.push(
      <a className="text-ink transition-colors hover:underline" href={`mailto:${email}`} key="email">
        {email}
      </a>,
    );
  }

  return (
    <footer
      className={`flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-xs text-muted ${className}`}
    >
      {parts.map((part, i) => (
        // A separator between items only, never leading or trailing.
        <span className="flex items-center gap-2" key={i}>
          {i > 0 ? (
            <span aria-hidden="true" className="text-rule">
              ·
            </span>
          ) : null}
          {part}
        </span>
      ))}
    </footer>
  );
}
